import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Card,
  Collapsible,
  Heading,
  Icon,
  Image,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconChevronDownFilled, IconChevronUpFilled } from '@zillow/constellation-icons';
import { LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { LeaseStatus } from '@zg-rentals/rental-manager-env';

import UploadedLeaseControls from './UploadedLease/UploadedLeaseControls';
import LeaseUploader from '../../../components/LeaseUploader';
import { SELF_SERVE_SECTION, STRING_CONSTANTS } from './constants';
import { LeaseTypes } from '../../../models/Types';
import type { Lease } from '../../../models';
import {
  useDeleteSelfUploadedDocumentMutation,
  useReorderLeaseDocumentsMutation,
  useUploadedFileDetailsQuery,
} from '../../../utils/queries';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';

type Props = {
  canDelete?: boolean;
  lease: Lease;
};

export const MAXIMUM_NUMBER_OF_DOCUMENTS = 10;

function UploadedLeaseCardSkeleton({
  marginTop,
  marginBottom,
}: {
  marginTop?: string | number | 0;
  marginBottom?: string;
}) {
  return (
    <Card
      tone="neutral"
      elevated={false}
      css={{
        marginTop: marginTop === 0 ? 0 : marginTop || undefined,
        marginBottom: marginBottom || undefined,
      }}
    >
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 'tighter',
          alignItems: 'center',
          height: '3rem',
        }}
      >
        <Box css={{ gridColumnEnd: 'span 1' }}>
          <LoadingText isLoading loadingWidth="3rem">
            Loading
          </LoadingText>
        </Box>
        <Box
          css={{
            gridColumnEnd: { base: 'span 5', md: 'span 3' },
            height: '3rem',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LoadingText isLoading loadingWidth="100%">
            Loading
          </LoadingText>
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 5', md: 'span 7' } }}>
          <LoadingText isLoading loadingWidth="70%">
            Loading
          </LoadingText>
        </Box>
        <Box css={{ gridColumnEnd: 'span 1', justifySelf: 'end' }}>
          <LoadingText isLoading loadingWidth="3rem">
            Loading
          </LoadingText>
        </Box>
      </Box>
    </Card>
  );
}

function UploaderSkeleton() {
  return (
    <Card tone="neutral" elevated={false}>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'loose',
          padding: 'loosest',
        }}
      >
        <LoadingText isLoading loadingWidth="80%">
          Loading
        </LoadingText>
        <LoadingText isLoading loadingWidth="20rem">
          Loading
        </LoadingText>
        <LoadingText isLoading loadingWidth="60%">
          Loading
        </LoadingText>
      </Box>
    </Card>
  );
}

export function UploadedLease({ canDelete, lease }: PropsWithChildren<Props>) {
  const { leaseId } = useLeasingUrlParams();
  const { enqueueToast } = useToast();
  const reorderLeaseDocumentsMutation = useReorderLeaseDocumentsMutation();
  const [uploadingCount, setUploadingCount] = useState(0);
  const [expectedFileCount, setExpectedFileCount] = useState(0);

  const onFileDeletedToast = useCallback(() => {
    enqueueToast(<Toast tone="info">File has been deleted</Toast>);
  }, [enqueueToast]);

  const onLeaseDeletedToast = useCallback(() => {
    enqueueToast(<Toast tone="info">The lease has been deleted</Toast>);
  }, [enqueueToast]);

  const deleteSelfUploadedDocumentMutation = useDeleteSelfUploadedDocumentMutation();
  const uploadedFilesQuery = useUploadedFileDetailsQuery(leaseId);

  const uploadedFileDetails = uploadedFilesQuery?.data;

  const [files, setFiles] = useState(uploadedFileDetails);

  useEffect(() => {
    if (uploadingCount > 0) {
      setExpectedFileCount((files?.length || 0) + uploadingCount);
    }
  }, [uploadingCount, files?.length]);

  useEffect(() => {
    if (files && files.length >= expectedFileCount) {
      setExpectedFileCount(0);
    }
  }, [files, expectedFileCount]);

  useEffect(() => {
    if (uploadedFileDetails) {
      setFiles(uploadedFileDetails);
    }
  }, [uploadedFileDetails]);

  async function onDelete(documentId: number) {
    await deleteSelfUploadedDocumentMutation.mutateAsync({
      postData: { leaseId: leaseId as unknown as number, documentId },
    });
    if (documentId) {
      onFileDeletedToast();
    } else {
      onLeaseDeletedToast();
    }
  }

  function renderAddMoreDocuments() {
    return (
      <Box css={{ marginTop: 'tight' }}>
        <LeaseUploader
          onFilesUploading={setUploadingCount}
          isSignedSelfUpload={lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD}
        />
      </Box>
    );
  }

  const onMoveFile = useCallback(
    (startIndex: number, targetIndex: number) => {
      if (!files) return;

      const updatedFiles = [...files];
      const fileBeingMoved = updatedFiles[startIndex];

      updatedFiles.splice(startIndex, 1);
      updatedFiles.splice(targetIndex, 0, fileBeingMoved);
      setFiles(updatedFiles);
    },
    [files],
  );

  const onDropComplete = useCallback(() => {
    if (!files) return;

    reorderLeaseDocumentsMutation.mutate({
      postData: {
        leaseId: leaseId as unknown as number,
        order: files.map((file) => file.id),
      },
    });
  }, [files, reorderLeaseDocumentsMutation, leaseId]);

  function renderCombinedLease() {
    if (files?.length > 0) {
      const combinedLeaseDocumentDetails = files[0];
      return (
        <Card tone="neutral" elevated={false}>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter', alignItems: 'center' }}>
            <Box css={{ gridColumnEnd: 'span 1' }} />
            <Box css={{ gridColumnEnd: { base: 'span 5', md: 'span 3' } }}>
              <Image
                alt="Document preview"
                src={combinedLeaseDocumentDetails.imageUrl}
                css={{ objectFit: 'contain', maxHeight: '3rem' }}
              />
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 5', md: 'span 7' } }}>
              <TextButton asChild>
                <a href={lease.getPDFLink(false)}>{lease.getDownloadFileName()}</a>
              </TextButton>
            </Box>
          </Box>
        </Card>
      );
    }
    return null;
  }

  function renderDragAndDrop() {
    if (uploadedFilesQuery.isPending || !uploadedFileDetails) {
      return (
        <>
          {Array.from({ length: 3 }).map((_, index) => (
            <UploadedLeaseCardSkeleton
              key={`skeleton-${index}`}
              marginTop={index === 0 ? 0 : 'tight'}
              marginBottom={index === 2 ? 'tight' : undefined}
            />
          ))}
          <UploaderSkeleton />
        </>
      );
    }

    if (lease?.status === LeaseStatus.SIGNING_COMPLETE) {
      return (
        <>
          {renderCombinedLease()}
          {files?.length > 1 && (
            <>
              <Box css={{ marginTop: 'tight' }} />
              <Collapsible>
                {(collapsed, toggle) => (
                  <>
                    <TextButton
                      onClick={() => toggle(!collapsed)}
                      iconPosition="end"
                      icon={
                        collapsed ? (
                          <Icon role="img" size="lg">
                            <IconChevronDownFilled />
                          </Icon>
                        ) : (
                          <Icon role="img" size="lg">
                            <IconChevronUpFilled />
                          </Icon>
                        )
                      }
                      css={{ marginBottom: 'tight' }}
                    >
                      View signed documents separately
                    </TextButton>
                    {collapsed
                      ? null
                      : files?.map((leaseDetail, index) => (
                          <UploadedLeaseControls
                            key={leaseDetail.imageUrl}
                            canEdit={canDelete}
                            index={index}
                            onMoveFile={onMoveFile}
                            onDropComplete={onDropComplete}
                            uploadedFileDetails={leaseDetail}
                            onDelete={onDelete.bind(this, leaseDetail.id)}
                            lease={lease}
                          />
                        ))}
                  </>
                )}
              </Collapsible>
            </>
          )}
        </>
      );
    }

    const skeletonsToShow = Math.max(0, expectedFileCount - (files?.length || 0));

    return (
      <>
        {files?.length > 0 ? (
          <>
            {files?.map((leaseDetail, index) => (
              <UploadedLeaseControls
                key={leaseDetail.imageUrl}
                canEdit={canDelete}
                index={index}
                onMoveFile={onMoveFile}
                onDropComplete={onDropComplete}
                uploadedFileDetails={leaseDetail}
                onDelete={onDelete.bind(this, leaseDetail.id)}
                lease={lease}
                marginBottom={index === files.length - 1 && skeletonsToShow === 0 ? 'tight' : undefined}
              />
            ))}
            {skeletonsToShow > 0 &&
              Array.from({ length: skeletonsToShow }).map((_, index) => (
                <UploadedLeaseCardSkeleton
                  key={`uploading-${index}`}
                  marginTop="tight"
                  marginBottom={index === skeletonsToShow - 1 ? 'tight' : undefined}
                />
              ))}
            {canDelete && renderAddMoreDocuments()}
          </>
        ) : (
          <>
            {skeletonsToShow > 0 &&
              Array.from({ length: skeletonsToShow }).map((_, index) => (
                <UploadedLeaseCardSkeleton
                  key={`uploading-empty-${index}`}
                  marginTop={index === 0 ? 0 : 'tight'}
                  marginBottom={index === skeletonsToShow - 1 ? 'tight' : undefined}
                />
              ))}
            <LeaseUploader
              onFilesUploading={setUploadingCount}
              isSignedSelfUpload={lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD}
            />
          </>
        )}
      </>
    );
  }

  function renderHeaderText() {
    return lease?.status === LeaseStatus.SIGNING_COMPLETE
      ? lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_ALREADY_SIGNED_MULTI_FILE_COMPLETE].heading
        : STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_MULTI_FILE_SIGNED].heading
      : lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_ALREADY_SIGNED_MULTI_FILE].heading
        : STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_MULTI_FILE].heading;
  }

  function renderDescriptionText() {
    return lease?.status === LeaseStatus.SIGNING_COMPLETE
      ? lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_ALREADY_SIGNED_MULTI_FILE_COMPLETE].description
        : STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_MULTI_FILE_SIGNED].description
      : lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_ALREADY_SIGNED_MULTI_FILE].description
        : STRING_CONSTANTS[SELF_SERVE_SECTION.LEASE_UPLOAD_MULTI_FILE].description;
  }

  const hasUploadedAFile = uploadedFilesQuery?.data?.length > 0;
  const isLoading = uploadedFilesQuery.isPending;

  return (
    <Card elevated={false} tone="neutral">
      <Box
        css={{
          display: 'flex',
          alignItems: { base: 'center', mdDown: 'flex-start' },
          justifyContent: 'space-between',
          gap: 'tight',
          marginBottom: 'tighter',
        }}
      >
        {isLoading ? (
          <LoadingHeading isLoading level={2} loadingWidth="60%">
            Loading
          </LoadingHeading>
        ) : (
          <Heading level={2} css={{ textStyle: 'responsive.heading-sm' }}>
            {renderHeaderText()}
          </Heading>
        )}
        {isLoading ? (
          <LoadingText isLoading loadingWidth="100px">
            Loading
          </LoadingText>
        ) : (
          <Tag tone={hasUploadedAFile ? 'success' : 'warning'} css={{ textTransform: 'capitalize' }}>
            {hasUploadedAFile ? 'Complete' : 'Incomplete'}
          </Tag>
        )}
      </Box>
      {isLoading ? (
        <>
          <LoadingText isLoading loadingWidth="90%">
            Loading
          </LoadingText>
          <Box css={{ marginBottom: 'tight' }} />
        </>
      ) : (
        <Paragraph css={{ marginBottom: 'tight' }}>{renderDescriptionText()}</Paragraph>
      )}
      {renderDragAndDrop()}
    </Card>
  );
}

export default UploadedLease;
